import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  useGfsOfferConfigGetQuery,
  useGfsOfferConfigPutMutation,
  updateGfsOfferConfigCache,
} from "@/services/backend/gfs/offer/api";
import {
  GfsOfferConfig,
  GfsOfferParameters,
  PutGfsOfferConfigRequest,
} from "@/services/backend/gfs/offer/types";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import type { RtkQueryMutation } from "@/shared/lib/debounce/debounce";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

const defaults: GfsOfferParameters = {
  sachkundePricePerPerson: 0,
  documentationFlatFee: 0,
  targetRevenuePerHour: 90,
  travelFlatFee: 0,
  disposalFlatFee: 0,
};

export function GfsOfferParametersPage() {
  const { companyId } = useActiveCompany();
  const { data, error } = useGfsOfferConfigGetQuery({ companyId });
  if (error) return <RTKQueryErrorAlert error={error} />;
  if (!data) return <Loader2 className="animate-spin" />;
  return <GfsOfferParametersAutosave config={data} companyId={companyId} />;
}

function GfsOfferParametersAutosave({
  config,
  companyId,
}: {
  config: GfsOfferConfig;
  companyId: string;
}) {
  const initialRequest = useMemo(
    () => ({
      companyId,
      config: { ...config, parameters: { ...defaults, ...config.parameters } },
    }),
    [companyId, config],
  );
  const [put, state] = useGfsOfferConfigPutMutation();
  const typed = [put, state] as unknown as RtkQueryMutation<
    PutGfsOfferConfigRequest,
    GfsOfferConfig
  >;
  const [setConfig] = useOptimisticDebouncedMutation<
    GfsOfferConfig,
    PutGfsOfferConfigRequest,
    GfsOfferConfig
  >(
    initialRequest,
    (nextConfig) => updateGfsOfferConfigCache(companyId, nextConfig),
    typed,
    {
      delay: 500,
      toastError: true,
    },
  );
  const parameters = initialRequest.config.parameters ?? defaults;
  const update = (patch: Partial<GfsOfferParameters>) =>
    setConfig(
      {
        companyId,
        config: {
          ...initialRequest.config,
          parameters: { ...parameters, ...patch },
        },
      },
      { ...initialRequest.config, parameters: { ...parameters, ...patch } },
    );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Parameter</CardTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Wiederverwendbare Werte für neue GfS-Angebote und Kalkulationen.
          </p>
        </div>
        <PersistenceStateUsingContext />
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <ParameterField
          label="Sachkundeschulung pro Person"
          value={parameters.sachkundePricePerPerson}
          onChange={(value) => update({ sachkundePricePerPerson: value })}
        />
        <ParameterField
          label="Dokumentationspauschale"
          value={parameters.documentationFlatFee}
          onChange={(value) => update({ documentationFlatFee: value })}
        />
        <ParameterField
          label="Zielumsatz pro Stunde"
          value={parameters.targetRevenuePerHour}
          onChange={(value) => update({ targetRevenuePerHour: value })}
        />
        <ParameterField
          label="Fahrtkostenpauschale"
          value={parameters.travelFlatFee}
          onChange={(value) => update({ travelFlatFee: value })}
        />
        <ParameterField
          label="Entsorgungspauschale"
          value={parameters.disposalFlatFee}
          onChange={(value) => update({ disposalFlatFee: value })}
        />
      </CardContent>
    </Card>
  );
}

function ParameterField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="grid gap-1.5 text-sm font-medium">
      {label}
      <div className="flex items-center gap-2">
        <FloatNumberInput
          className="w-full"
          value={value}
          onChange={onChange}
        />
        <span className="text-muted-foreground">€</span>
      </div>
    </label>
  );
}
